/**
 * AuthCallback — Emergent-managed Google Auth landing page.
 *
 * Emergent redirects to `<origin>/auth/callback#session_id=<sid>` after the
 * user completes the Google flow. This component:
 *   1. Reads `session_id` from the URL fragment (synchronously, once).
 *   2. Exchanges it via POST /api/auth/google for our own Nexus JWT.
 *   3. Persists the JWT via AuthContext.updateUser() + localStorage.
 *   4. Redirects to `/w/{default_workspace_id}` or `/home` as a fallback.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { NexusLogo } from '../components/brand/NexusLogo';
import { extractErrorMessage } from '../lib/errors';

export default function AuthCallback() {
  const nav = useNavigate();
  const { updateUser } = useAuth();
  const processed = useRef(false);
  const [status, setStatus] = useState<'processing' | 'error'>('processing');
  const [errMsg, setErrMsg] = useState<string>('');

  useEffect(() => {
    // Race protection: only run once even under React StrictMode.
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const session_id = match ? decodeURIComponent(match[1]) : null;

    if (!session_id) {
      setStatus('error');
      setErrMsg('No recibimos el identificador de sesión de Google.');
      return;
    }

    // Clean the hash from the URL so the token is not left in history.
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch { /* no-op */ }

    (async () => {
      try {
        const resp = await apiService.exchangeGoogleSession(session_id);
        localStorage.setItem('nexus_token', resp.access_token);
        updateUser(resp.user);
        toast.success('¡Bienvenido a Nexus!');
        // v1.11 · Google users also land on /home (main hub).
        nav('/home', { replace: true });
      } catch (err: any) {
        const msg = extractErrorMessage(err, 'No pudimos iniciar sesión con Google.');
        setErrMsg(msg);
        setStatus('error');
        toast.error(msg);
      }
    })();
  }, [nav, updateUser]);

  return (
    <main
      className="min-h-[100dvh] w-full flex flex-col items-center justify-center px-6 py-10 nx-circuit"
      data-testid="auth-callback-page"
    >
      <div className="flex flex-col items-center gap-4 max-w-md w-full text-center">
        <NexusLogo size={64} glow />
        {status === 'processing' ? (
          <>
            <div className="text-lg font-medium" style={{ color: 'hsl(var(--text))' }}>
              Verificando tu cuenta de Google…
            </div>
            <div className="text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
              Esto toma solo un momento.
            </div>
            <Loader2 className="w-6 h-6 mt-3 animate-spin" style={{ color: 'hsl(var(--accent))' }} />
          </>
        ) : (
          <>
            <div className="text-lg font-medium" style={{ color: 'hsl(var(--text))' }}>
              No pudimos iniciar sesión
            </div>
            <div className="text-sm max-w-sm" style={{ color: 'hsl(var(--text-muted))' }}>
              {errMsg}
            </div>
            <button
              data-testid="auth-callback-retry"
              onClick={() => nav('/auth', { replace: true })}
              className="mt-4 nx-btn-primary"
            >
              <ShieldCheck className="w-4 h-4" />
              Volver a intentar
            </button>
          </>
        )}
      </div>
    </main>
  );
}
